"use client";

import { c as _c } from "react/compiler-runtime";
import { ImagePlus, Loader2, Sparkles, X } from "lucide-react";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { AspectRatioIcon } from "@/components/playground/aspect-ratio-icon";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getModelImageConfig } from "@/lib/image-gen";
import { useI18n } from "@llmgateway/i18n/react";
import type { AspectRatio } from "@/lib/image-gen";
interface InputImage {
  dataUrl: string;
  mediaType: string;
}
interface ImageControlsProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  selectedModels: string[];
  imageAspectRatio: AspectRatio;
  setImageAspectRatio: (value: AspectRatio) => void;
  imageSize: string;
  setImageSize: (value: string) => void;
  alibabaImageSize: string;
  setAlibabaImageSize: (value: string) => void;
  imageQuality: string;
  setImageQuality: (value: string) => void;
  imageCount: 1 | 2 | 3 | 4;
  setImageCount: (value: 1 | 2 | 3 | 4) => void;
  isGenerating: boolean;
  onGenerate: () => void;
  isEditModel: boolean;
  requiresImageInput: boolean;
  inputImages: InputImage[];
  setInputImages: Dispatch<SetStateAction<InputImage[]>>;
}
const aspectRatios: AspectRatio[] = ["auto", "1:1", "9:16", "16:9", "3:4", "4:3", "3:2", "2:3", "5:4", "4:5", "21:9", "1:4", "4:1", "1:8", "8:1"];
export function ImageControls(t0) {
  const $ = _c(134);
  const {
    prompt,
    setPrompt,
    selectedModels,
    imageAspectRatio,
    setImageAspectRatio,
    imageSize,
    setImageSize,
    alibabaImageSize,
    setAlibabaImageSize,
    imageQuality,
    setImageQuality,
    imageCount,
    setImageCount,
    isGenerating,
    onGenerate,
    isEditModel,
    requiresImageInput,
    inputImages,
    setInputImages
  } = t0;
  const {
    t
  } = useI18n();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const primaryModel = selectedModels[0] ?? "";
  const config = getModelImageConfig(primaryModel);
  const maxInputImages = config.maxInputImages;
  const filteredAspectRatios = config.supportedAspectRatios ?? aspectRatios;
  useEffect(() => {
    if (config.supportedAspectRatios && !config.supportedAspectRatios.includes(imageAspectRatio)) {
      setImageAspectRatio("auto");
    }
  }, [config.supportedAspectRatios, imageAspectRatio, setImageAspectRatio]);
  let t1;
  if ($[0] !== isEditModel || $[1] !== maxInputImages || $[2] !== setInputImages) {
    t1 = file => {
      if (!file.type.startsWith("image/")) {
        return;
      }
      if (!isEditModel) {
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setInputImages(prev => {
          if (prev.length >= maxInputImages) {
            return prev;
          }
          return [...prev, {
            dataUrl: reader.result as string,
            mediaType: file.type
          }];
        });
      };
      reader.readAsDataURL(file);
    };
    $[0] = isEditModel;
    $[1] = maxInputImages;
    $[2] = setInputImages;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const addImageFile = t1;
  let t2;
  if ($[4] !== isGenerating || $[5] !== onGenerate || $[6] !== prompt) {
    t2 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (prompt.trim() && !isGenerating && canGenerate) {
          onGenerate();
        }
      }
    };
    $[4] = isGenerating;
    $[5] = onGenerate;
    $[6] = prompt;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleKeyDown = t2;
  let t3;
  if ($[8] !== addImageFile || $[9] !== inputImages.length || $[10] !== maxInputImages) {
    t3 = e_0 => {
      const files = Array.from(e_0.target.files ?? []);
      for (const file_0 of files) {
        if (inputImages.length >= maxInputImages) {
          break;
        }
        addImageFile(file_0);
      }
      e_0.target.value = "";
    };
    $[8] = addImageFile;
    $[9] = inputImages.length;
    $[10] = maxInputImages;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleFileSelect = t3;
  let t4;
  if ($[12] !== addImageFile || $[13] !== inputImages.length || $[14] !== isEditModel || $[15] !== maxInputImages) {
    t4 = e_1 => {
      if (!isEditModel || inputImages.length >= maxInputImages) {
        return;
      }
      const items = Array.from(e_1.clipboardData.items);
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e_1.preventDefault();
          const file_1 = item.getAsFile();
          if (file_1) {
            addImageFile(file_1);
          }
          break;
        }
      }
    };
    $[12] = addImageFile;
    $[13] = inputImages.length;
    $[14] = isEditModel;
    $[15] = maxInputImages;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handlePaste = t4;
  let t5;
  if ($[17] !== inputImages.length || $[18] !== isEditModel || $[19] !== maxInputImages) {
    t5 = e_2 => {
      e_2.preventDefault();
      e_2.stopPropagation();
      if (isEditModel && inputImages.length < maxInputImages) {
        setIsDragging(true);
      }
    };
    $[17] = inputImages.length;
    $[18] = isEditModel;
    $[19] = maxInputImages;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const handleDragOver = t5;
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_3 => {
      e_3.preventDefault();
      e_3.stopPropagation();
      if (dropZoneRef.current && !dropZoneRef.current.contains(e_3.relatedTarget as Node)) {
        setIsDragging(false);
      }
    };
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleDragLeave = t6;
  let t7;
  if ($[22] !== addImageFile || $[23] !== inputImages.length || $[24] !== isEditModel || $[25] !== maxInputImages) {
    t7 = e_4 => {
      e_4.preventDefault();
      e_4.stopPropagation();
      setIsDragging(false);
      if (!isEditModel || inputImages.length >= maxInputImages) {
        return;
      }
      const files_0 = Array.from(e_4.dataTransfer.files);
      for (const file_2 of files_0) {
        if (file_2.type.startsWith("image/")) {
          addImageFile(file_2);
        }
      }
    };
    $[22] = addImageFile;
    $[23] = inputImages.length;
    $[24] = isEditModel;
    $[25] = maxInputImages;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const handleDrop = t7;
  let t8;
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const handleWindowDragEnd = () => setIsDragging(false);
      window.addEventListener("dragend", handleWindowDragEnd);
      return () => window.removeEventListener("dragend", handleWindowDragEnd);
    };
    t9 = [];
    $[27] = t8;
    $[28] = t9;
  } else {
    t8 = $[27];
    t9 = $[28];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[29] !== maxInputImages || $[30] !== setInputImages) {
    t10 = () => {
      setInputImages(prev_0 => prev_0.length > maxInputImages ? prev_0.slice(0, maxInputImages) : prev_0);
    };
    t11 = [maxInputImages, setInputImages];
    $[29] = maxInputImages;
    $[30] = setInputImages;
    $[31] = t10;
    $[32] = t11;
  } else {
    t10 = $[31];
    t11 = $[32];
  }
  useEffect(t10, t11);
  let t12;
  if ($[33] !== setInputImages) {
    t12 = index => {
      setInputImages(prev_1 => prev_1.filter((_, i) => i !== index));
    };
    $[33] = setInputImages;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const removeImage = t12;
  const canGenerate = prompt.trim().length > 0 && selectedModels.length > 0;
  const t13 = "border-b bg-background p-4";
  const t14 = "max-w-4xl mx-auto space-y-3";
  const t15 = `rounded-md border-input border dark:bg-input/30 shadow-xs focus-within:ring-1 focus-within:ring-ring transition-colors ${isDragging ? "border-primary bg-primary/5 ring-1 ring-primary" : ""}`;
  let t16;
  if ($[35] !== inputImages.length || $[36] !== isDragging || $[37] !== isEditModel || $[38] !== maxInputImages || $[39] !== t) {
    t16 = isDragging && isEditModel && inputImages.length < maxInputImages && <div className="flex items-center justify-center py-4 text-sm text-muted-foreground"><ImagePlus className="h-4 w-4 mr-2" />{t("studio.dropImageHere")}</div>;
    $[35] = inputImages.length;
    $[36] = isDragging;
    $[37] = isEditModel;
    $[38] = maxInputImages;
    $[39] = t;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== inputImages || $[42] !== isEditModel || $[43] !== removeImage || $[44] !== t) {
    t17 = isEditModel && inputImages.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{inputImages.map((img, i_0) => <div key={i_0} className="relative group h-14 w-14 rounded-md border"><img src={img.dataUrl} alt={`${t("studio.addImage")} ${i_0 + 1}`} className="size-full rounded-md object-cover" /><button type="button" aria-label={t("studio.removeAttachment")} onClick={() => removeImage(i_0)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:text-foreground"><X className="h-3 w-3" /></button></div>)}</div>;
    $[41] = inputImages;
    $[42] = isEditModel;
    $[43] = removeImage;
    $[44] = t;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== setPrompt) {
    t18 = e_5 => setPrompt(e_5.target.value);
    $[46] = setPrompt;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== isEditModel || $[49] !== requiresImageInput || $[50] !== t) {
    t19 = requiresImageInput ? t("studio.describeImageEdit") : isEditModel ? t("studio.describeImageWithOptionalInput") : t("studio.describeImage");
    $[48] = isEditModel;
    $[49] = requiresImageInput;
    $[50] = t;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== handleKeyDown || $[53] !== handlePaste || $[54] !== isGenerating || $[55] !== prompt || $[56] !== t18 || $[57] !== t19) {
    t20 = <Textarea ref={textareaRef} value={prompt} onChange={t18} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={t19} className="min-h-[80px] max-h-[200px] resize-none border-0 bg-transparent dark:bg-transparent focus-visible:ring-0 shadow-none" disabled={isGenerating} />;
    $[52] = handleKeyDown;
    $[53] = handlePaste;
    $[54] = isGenerating;
    $[55] = prompt;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== handleDragLeave || $[60] !== handleDragOver || $[61] !== handleDrop || $[62] !== t15 || $[63] !== t16 || $[64] !== t17 || $[65] !== t20) {
    t21 = <div ref={dropZoneRef} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={t15}>{t16}{t17}{t20}</div>;
    $[59] = handleDragLeave;
    $[60] = handleDragOver;
    $[61] = handleDrop;
    $[62] = t15;
    $[63] = t16;
    $[64] = t17;
    $[65] = t20;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== handleFileSelect) {
    t22 = <input ref={fileInputRef} type="file" accept="image/*" multiple={true} className="hidden" onChange={handleFileSelect} />;
    $[67] = handleFileSelect;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  const t23 = "flex flex-wrap items-center gap-2";
  let t24;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => fileInputRef.current?.click();
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  const t25 = isGenerating || !isEditModel || inputImages.length >= maxInputImages;
  let t26;
  if ($[70] !== isEditModel || $[71] !== t) {
    t26 = !isEditModel ? t("studio.imageInputUnsupported") : undefined;
    $[70] = isEditModel;
    $[71] = t;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <ImagePlus className="h-4 w-4 mr-1.5" />;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== inputImages.length || $[75] !== maxInputImages || $[76] !== t) {
    t28 = inputImages.length === 0 ? t("studio.addImage") : `${inputImages.length}/${maxInputImages}`;
    $[74] = inputImages.length;
    $[75] = maxInputImages;
    $[76] = t;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== t25 || $[79] !== t26 || $[80] !== t28) {
    t29 = <Button variant="outline" size="sm" onClick={t24} disabled={t25} title={t26}>{t27}{t28}</Button>;
    $[78] = t25;
    $[79] = t26;
    $[80] = t28;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  const t30 = !config.usesPixelDimensions && <><Select value={imageAspectRatio} onValueChange={val => setImageAspectRatio(val as AspectRatio)}><SelectTrigger size="sm" className="min-w-[110px]"><SelectValue placeholder={t("studio.aspectRatio")} /></SelectTrigger><SelectContent>{filteredAspectRatios.map(r => <SelectItem key={r} value={r}><span className="flex items-center gap-2"><AspectRatioIcon ratio={r} />{r === "auto" ? t("studio.auto") : r}</span></SelectItem>)}</SelectContent></Select>{!config.isReve && <Select value={imageSize} onValueChange={setImageSize}><SelectTrigger size="sm" className="min-w-[80px]"><SelectValue placeholder={t("studio.resolution")} /></SelectTrigger><SelectContent>{config.availableSizes.map(_temp)}</SelectContent></Select>}</>;
  let t31;
  if ($[82] !== alibabaImageSize || $[83] !== config.availableSizes || $[84] !== config.isGptImage || $[85] !== config.usesPixelDimensions || $[86] !== setAlibabaImageSize || $[87] !== t) {
    t31 = config.usesPixelDimensions && config.isGptImage && <Select value={alibabaImageSize} onValueChange={setAlibabaImageSize}><SelectTrigger size="sm" className="min-w-[130px]"><SelectValue placeholder={t("studio.resolution")} /></SelectTrigger><SelectContent>{config.availableSizes.map(size_0 => <SelectItem key={size_0} value={size_0}>{size_0 === "auto" ? t("studio.auto") : size_0}</SelectItem>)}</SelectContent></Select>;
    $[82] = alibabaImageSize;
    $[83] = config.availableSizes;
    $[84] = config.isGptImage;
    $[85] = config.usesPixelDimensions;
    $[86] = setAlibabaImageSize;
    $[87] = t;
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  let t32;
  if ($[89] !== alibabaImageSize || $[90] !== config.isGptImage || $[91] !== config.usesPixelDimensions || $[92] !== setAlibabaImageSize || $[93] !== t) {
    t32 = config.usesPixelDimensions && !config.isGptImage && <Select value={alibabaImageSize} onValueChange={setAlibabaImageSize}><SelectTrigger size="sm" className="min-w-[130px]"><SelectValue placeholder={t("studio.imageSize")} /></SelectTrigger><SelectContent><SelectItem value="1024x1024">1024x1024</SelectItem><SelectItem value="720x1280">720x1280</SelectItem><SelectItem value="1280x720">1280x720</SelectItem><SelectItem value="1024x1536">1024x1536</SelectItem><SelectItem value="1536x1024">1536x1024</SelectItem><SelectItem value="2048x1024">2048x1024</SelectItem><SelectItem value="1024x2048">1024x2048</SelectItem></SelectContent></Select>;
    $[89] = alibabaImageSize;
    $[90] = config.isGptImage;
    $[91] = config.usesPixelDimensions;
    $[92] = setAlibabaImageSize;
    $[93] = t;
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  let t33;
  if ($[95] !== config.availableQualities || $[96] !== config.supportsQuality || $[97] !== imageQuality || $[98] !== setImageQuality || $[99] !== t) {
    t33 = config.supportsQuality && <Select value={imageQuality} onValueChange={setImageQuality}><SelectTrigger size="sm" className="min-w-[100px]"><SelectValue placeholder={t("studio.quality")} /></SelectTrigger><SelectContent>{config.availableQualities.map(_temp2)}</SelectContent></Select>;
    $[95] = config.availableQualities;
    $[96] = config.supportsQuality;
    $[97] = imageQuality;
    $[98] = setImageQuality;
    $[99] = t;
    $[100] = t33;
  } else {
    t33 = $[100];
  }
  const t34 = String(imageCount);
  let t35;
  if ($[101] !== setImageCount) {
    t35 = val_0 => setImageCount(Number(val_0) as 1 | 2 | 3 | 4);
    $[101] = setImageCount;
    $[102] = t35;
  } else {
    t35 = $[102];
  }
  let t36;
  if ($[103] !== t) {
    t36 = t("studio.count");
    $[103] = t;
    $[104] = t36;
  } else {
    t36 = $[104];
  }
  let t37;
  if ($[105] !== t36) {
    t37 = <SelectTrigger size="sm" className="min-w-[90px]"><SelectValue placeholder={t36} /></SelectTrigger>;
    $[105] = t36;
    $[106] = t37;
  } else {
    t37 = $[106];
  }
  let t38;
  if ($[107] !== t) {
    t38 = <SelectContent>{([1, 2, 3, 4] as const).map(count => <SelectItem key={count} value={String(count)}>{t("studio.imageCount", {
          count
        })}</SelectItem>)}</SelectContent>;
    $[107] = t;
    $[108] = t38;
  } else {
    t38 = $[108];
  }
  let t39;
  if ($[109] !== t34 || $[110] !== t35 || $[111] !== t37 || $[112] !== t38) {
    t39 = <Select value={t34} onValueChange={t35}>{t37}{t38}</Select>;
    $[109] = t34;
    $[110] = t35;
    $[111] = t37;
    $[112] = t38;
    $[113] = t39;
  } else {
    t39 = $[113];
  }
  let t40;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="flex-1" />;
    $[114] = t40;
  } else {
    t40 = $[114];
  }
  const t41 = isGenerating || !canGenerate;
  let t42;
  if ($[115] !== isGenerating || $[116] !== t) {
    t42 = isGenerating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("common.generating")}</> : <><Sparkles className="h-4 w-4 mr-2" />{t("common.generate")}</>;
    $[115] = isGenerating;
    $[116] = t;
    $[117] = t42;
  } else {
    t42 = $[117];
  }
  let t43;
  if ($[118] !== onGenerate || $[119] !== t41 || $[120] !== t42) {
    t43 = <Button onClick={onGenerate} disabled={t41} className="min-w-[120px]">{t42}</Button>;
    $[118] = onGenerate;
    $[119] = t41;
    $[120] = t42;
    $[121] = t43;
  } else {
    t43 = $[121];
  }
  let t44;
  if ($[122] !== t29 || $[123] !== t30 || $[124] !== t31 || $[125] !== t32 || $[126] !== t33 || $[127] !== t39 || $[128] !== t43) {
    t44 = <div className={t23}>{t29}{t30}{t31}{t32}{t33}{t39}{t40}{t43}</div>;
    $[122] = t29;
    $[123] = t30;
    $[124] = t31;
    $[125] = t32;
    $[126] = t33;
    $[127] = t39;
    $[128] = t43;
    $[129] = t44;
  } else {
    t44 = $[129];
  }
  let t45;
  if ($[130] !== t21 || $[131] !== t22 || $[132] !== t44) {
    t45 = <div className={t13}><div className={t14}>{t21}{t22}{t44}</div></div>;
    $[130] = t21;
    $[131] = t22;
    $[132] = t44;
    $[133] = t45;
  } else {
    t45 = $[133];
  }
  return t45;
}
function _temp2(q) {
  return <SelectItem key={q} value={q}>{q.charAt(0).toUpperCase() + q.slice(1)}</SelectItem>;
}
function _temp(size) {
  return <SelectItem key={size} value={size}>{size}</SelectItem>;
}